import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UseToast } from '../../hooks/UseToast';
import Button from '../../components/ui/Button';
import { FiSave, FiX } from 'react-icons/fi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const ServiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = UseToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'FiCode',
    features: '',
    order: 0,
    isActive: true
  });

  useEffect(() => {
    if (id) fetchService();
  }, [id]);

  const fetchService = async () => {
    setFetching(true);
    try {
      const res = await fetch(`${API_URL}/services/${id}`);
      const data = await res.json();

      if (data.success) {
        const s = data.data;
        setFormData({
          title: s.title || '',
          description: s.description || '',
          icon: s.icon || 'FiCode',
          // Array iruntha join panni string-a kaatu, illa string-a iruntha athaye kaatu
          features: Array.isArray(s.features)? s.features.join(', ') : s.features || '',
          order: s.order || 0,
          isActive: s.isActive!== undefined? s.isActive : true
        });
      } else {
        toast.error('Service not found');
        navigate('/services');
      }
    } catch (error) {
      toast.error('Failed to load service');
      navigate('/services');
    }
    setFetching(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const url = id? `${API_URL}/services/${id}` : `${API_URL}/services`;

      // IMPORTANT FIX: features-a string la irunthu array va mathrom
      const payload = {
        title: formData.title,
        description: formData.description,
        icon: formData.icon,
        order: Number(formData.order),
        isActive: formData.isActive,
        features: formData.features
         .split(',')
         .map(f => f.trim())
         .filter(f => f!== '')
      };

      const res = await fetch(url, {
        method: id? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data.success) {
        toast.success(id? 'Service updated!' : 'Service created!');
        navigate('/services');
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  if (fetching) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <>
      <style>{`
.form-card { background: #FFF; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); max-width: 800px; }
.form-title { font-size: 24px; font-weight: 800; color: #0F172A; margin: 0 0 30px 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 20px; }
.form-group-full { grid-column: 1 / -1; }
.form-label { display: block; font-size: 14px; font-weight: 600; color: #334155; margin-bottom: 8px; }
.form-input,.form-select,.form-textarea { width: 100%; padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 15px; transition: all 0.3s ease; box-sizing: border-box; font-family: inherit; }
.form-input:focus,.form-select:focus,.form-textarea:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
.form-textarea { min-height: 120px; resize: vertical; }
.form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 30px; }
.checkbox-group { display: flex; align-items: center; gap: 8px; }
.checkbox-group input[type="checkbox"] { width: 20px; height: 20px; cursor: pointer; }
        @media (max-width: 768px) {.form-grid { grid-template-columns: 1fr; } }
      `}</style>
      <div>
        <div className="form-card">
          <h1 className="form-title">{id? 'Edit Service' : 'Add New Service'}</h1>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Service Title *</label>
                <input type="text" className="form-input" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Icon</label>
                <select className="form-select" value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})}>
                  <option value="FiCode">Code</option>
                  <option value="FiSmartphone">Mobile</option>
                  <option value="FiBriefcase">Business</option>
                  <option value="FiMonitor">Monitor</option>
                  <option value="FiShoppingCart">E-commerce</option>
                  <option value="FiSettings">Settings</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input type="number" className="form-input" value={formData.order} onChange={(e) => setFormData({...formData, order: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <div className="checkbox-group">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} />
                  <span>Active</span>
                </div>
              </div>
            </div>
            <div className="form-group-full">
              <label className="form-label">Description *</label>
              <textarea className="form-textarea" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
            </div>
            <div className="form-group-full">
              <label className="form-label">Features (comma separated)</label>
              <textarea className="form-textarea" placeholder="React, Node.js, MongoDB, AWS" value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} />
            </div>
            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => navigate('/services')} icon={<FiX />} disabled={loading}>Cancel</Button>
              <Button type="submit" icon={<FiSave />} disabled={loading}>{loading? 'Saving...' : id? 'Update' : 'Create'} Service</Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ServiceForm;